import { useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { ProgressBar } from 'primereact/progressbar'
import { useTranslation } from 'react-i18next'

// assets
import markerIcon from 'assets/icons/floor-location-icon.svg'
import locationGray from 'assets/icons/location-gray.svg'
import locationMyOffice from 'assets/icons/location.svg'
import locationCommon from 'assets/icons/location-shared.svg'
import { useHistory } from 'react-router-dom'

// local imports
import { IAllLocationList } from 'types/LocationType'
import { IAuthFloorListByLocation } from 'types/FloorType'
import { PlanTabs } from 'types/PlanTabs'
import useGetLocationsFloorList from './hooks/useGetFloorList'
import NoContent from 'components/ui/NoContent'
import Loader from 'components/ui/Loader'
import {
  ContentArea,
  HeaderArea,
  Header,
  InfoList,
  InfoListItem,
  SidebarActionArea,
  SidebarButton,
  SidebarContainer,
} from './styled'

interface FloorSidebarProps {
  startDate: Date
  endDate: Date
  selectedLocation: IAllLocationList
  handleClose: () => void
}

export default function FloorSidebar({ startDate, endDate, selectedLocation, handleClose }: FloorSidebarProps) {
  const { t } = useTranslation()
  const history = useHistory()
  const locationId = selectedLocation?.id
  const isMyFirm = selectedLocation.isMyFirmLocation
  const [selectedFloor, setSelectedFloor] = useState<IAuthFloorListByLocation | undefined>()

  /* GET Floor List */
  const { floorList, isLoading } = useGetLocationsFloorList(locationId, startDate.toISOString())
  /* GET Floor List */

  const customIcons = (
    <div className="d-flex justify-content-between align-items-center w-100">
      <Header>{t('sidebar.block-floor-info')}</Header>
      <button className="p-sidebar-icon p-link mr-2" onClick={handleClose}>
        <span className="pi pi-times" />
      </button>
    </div>
  )

  const setItemIcon = (isShared: boolean) => {
    if (isShared) return locationCommon
    else if (!isShared && isMyFirm) return locationMyOffice
    else return locationGray
  }

  /* Navigate to Reservation page */
  const handleNavigate = () => {
    history.push({
      pathname: '/reservation',
      state: {
        response: {
          selectedFloor: {
            ...selectedFloor,
            _fullLocation:
              selectedLocation?.name +
              ', ' +
              selectedLocation?.districtName +
              ', ' +
              selectedFloor?.blockName +
              ', ' +
              selectedFloor?.name,
            _floorName: selectedFloor?.name,
            _id: selectedFloor?.id,
          },
          startDate,
          endDate,
          isAbroad: { name: 'countries.turkey', isAbroad: false },
          planTab: selectedFloor?.isShared ? PlanTabs.COMMON : PlanTabs.OFFICE,
          isNew: true,
          routingType: 1,
        },
      },
    })
  }

  function renderItems(dt: IAuthFloorListByLocation): JSX.Element {
    return (
      <InfoListItem onClick={() => setSelectedFloor(dt)} key={dt.id} isSelected={dt.id === selectedFloor?.id}>
        <img src={setItemIcon(dt.isShared)} alt={dt.blockName} />
        <div className="item-content">
          <span className="sub-header">{t('most-used-words.block')}</span>
          <span className="sub-text"> {dt.blockName}</span>
        </div>
        <div className="item-content">
          <span className="sub-header">{t('most-used-words.floor')}</span>
          <span className="sub-text"> {dt.floorTemplateName}</span>
        </div>
        <div className="item-content">
          <span className="sub-header">{t('dashboard.occupancy')}</span>
          <ProgressBar
            value={dt.occupancy.percentage}
            showValue={false}
            style={{ height: '4px', margin: '4px 0' }}
            color="#9FAAE6"
          />
          <span className="sub-header">%{dt.occupancy.percentage} </span>
        </div>
      </InfoListItem>
    )
  }

  return (
    <Sidebar
      position="right"
      blockScroll
      showCloseIcon={false}
      icons={customIcons}
      style={{ width: '500px' }}
      className="of-sidebar of-bordered-sidebar-box"
      visible={!!selectedLocation}
      onHide={handleClose}>
      {isLoading ? (
        <Loader src={locationGray} />
      ) : (
        <SidebarContainer>
          <ContentArea>
            <HeaderArea>
              <div className="location-name">
                <img className="location-icon" src={markerIcon} alt="location-floor-icon" />
                {selectedLocation?.name}
              </div>
              <div className="firm-name">{selectedLocation?.firmName}</div>
              <div className="location-address">{selectedLocation?.address}</div>
            </HeaderArea>

            {!!floorList?.length ? (
              <InfoList>{floorList?.map((dt) => renderItems(dt))}</InfoList>
            ) : (
              <div>
                <NoContent message={t('sidebar.no-block-floor')} />
              </div>
            )}
          </ContentArea>

          <SidebarActionArea>
            <SidebarButton
              disabled={!selectedFloor}
              id="goToOfficeRes"
              label={t('buttons.go-to-office')}
              onClick={handleNavigate}
            />
          </SidebarActionArea>
        </SidebarContainer>
      )}
    </Sidebar>
  )
}
